import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Settings, 
  RefreshCw, 
  AlertCircle,
  CheckCircle,
  XCircle,
  TrendingUp,
  TrendingDown,
  DollarSign
} from 'lucide-react';
import { userDetailService, UserAPIConnection, UserTradingAccount } from '@/services/admin/user-detail-service';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';

interface UserTradingSetupTabProps {
  userId: string;
}

export const UserTradingSetupTab: React.FC<UserTradingSetupTabProps> = ({ userId }) => {
  const [apiConnections, setApiConnections] = useState<UserAPIConnection[]>([]);
  const [tradingAccounts, setTradingAccounts] = useState<UserTradingAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchData = async () => {
    try {
      const [connectionsData, accountsData] = await Promise.all([
        userDetailService.getUserAPIConnections(userId),
        userDetailService.getUserTradingAccounts(userId)
      ]);
      
      setApiConnections(connectionsData);
      setTradingAccounts(accountsData);
    } catch (error) {
      console.error('Error fetching trading setup data:', error);
      toast.error('Failed to load trading setup data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [userId]);

  const handleRefreshConnections = async () => {
    setIsRefreshing(true);
    try {
      const success = await userDetailService.refreshUserConnections(userId);
      if (success) {
        toast.success('Connection refresh initiated');
        await fetchData();
      } else {
        toast.error('Failed to refresh connections');
      }
    } catch (error) {
      toast.error('Error refreshing connections');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSyncBalances = async () => {
    try {
      const success = await userDetailService.syncUserBalances(userId);
      if (success) {
        toast.success('Balance sync initiated');
        await fetchData();
      } else {
        toast.error('Failed to sync balances');
      }
    } catch (error) {
      toast.error('Error syncing balances');
    }
  };

  const getConnectionStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'connected':
        return (
          <Badge className="bg-green-100 text-green-800 border-green-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            Connected
          </Badge>
        );
      case 'error':
        return (
          <Badge className="bg-red-100 text-red-800 border-red-200">
            <XCircle className="h-3 w-3 mr-1" />
            Error
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary">
            <AlertCircle className="h-3 w-3 mr-1" />
            {status}
          </Badge>
        );
    }
  };

  const getBalanceChange = (current: number, previous: number | null) => {
    if (!previous) return null;
    const change = current - previous;
    const percentage = ((change / previous) * 100).toFixed(2);
    return { change, percentage };
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-48" />
                  </div>
                  <Skeleton className="h-6 w-20" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* API Connections */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              API Connections
            </CardTitle>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRefreshConnections}
              disabled={isRefreshing}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh All
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {apiConnections.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Settings className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
              <p>No API connections found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {apiConnections.map((connection) => (
                <div key={connection.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-medium">{connection.apiName}</h3>
                      {getConnectionStatusBadge(connection.status)}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      <p>Created: {formatDistanceToNow(new Date(connection.createdAt), { addSuffix: true })}</p>
                      {connection.lastRefreshAttempt && (
                        <p>Last refresh: {formatDistanceToNow(new Date(connection.lastRefreshAttempt), { addSuffix: true })}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    {connection.refreshStatus && (
                      <Badge variant="outline" className="mb-2">
                        Refresh: {connection.refreshStatus}
                      </Badge>
                    )}
                    {connection.balanceSyncStatus && (
                      <Badge variant="outline">
                        Sync: {connection.balanceSyncStatus}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Trading Accounts */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Trading Accounts
            </CardTitle>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleSyncBalances}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Sync Balances
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {tradingAccounts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <DollarSign className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
              <p>No trading accounts found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {tradingAccounts.map((account) => {
                const balanceChange = getBalanceChange(account.balance, account.previousBalance);
                
                return (
                  <div key={account.id} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="font-medium">Account #{account.accountNumber}</h3>
                        <Badge variant={account.isLive ? 'default' : 'secondary'}>
                          {account.isLive ? 'Live' : 'Demo'}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Current Balance</span>
                        <span className="font-bold text-lg">${account.balance.toLocaleString()}</span>
                      </div>
                      
                      {balanceChange && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Change</span>
                          <div className="flex items-center gap-1">
                            {balanceChange.change > 0 ? (
                              <TrendingUp className="h-4 w-4 text-green-500" />
                            ) : (
                              <TrendingDown className="h-4 w-4 text-red-500" />
                            )}
                            <span className={`text-sm font-medium ${
                              balanceChange.change > 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              ${Math.abs(balanceChange.change).toLocaleString()} ({balanceChange.percentage}%)
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {account.lastBalanceUpdate && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Last Update</span>
                          <span className="text-sm">
                            {formatDistanceToNow(new Date(account.lastBalanceUpdate), { addSuffix: true })}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};